import type { Database } from '../lib/database.types';
import type { CampaignStatus as CanonicalCampaignStatus, DeliverableStatus } from '../constants/status';
import { statusOrder } from '../constants/status';

export type Campaign = Database['public']['Tables']['campaigns']['Row'];
export type CampaignInsert = Database['public']['Tables']['campaigns']['Insert'];
export type CampaignUpdate = Database['public']['Tables']['campaigns']['Update'];

// Re-export canonical types from constants
export type CampaignStatus = CanonicalCampaignStatus;
export type DeliveryStatus = DeliverableStatus;
export type CompanyInvoiceStatus = 'Not Issued' | 'Issued' | 'Paid' | 'Overdue' | 'Cancelled';
export type InfluencerInvoiceStatus = 'Not Requested' | 'Requested' | 'Received' | 'Paid';
export type ClientPaymentStatus = 'Unpaid' | 'Partially Paid' | 'Paid';

export const CAMPAIGN_STATUS_CONFIG = {
  Consultation: { color: 'gray', bgColor: 'bg-gray-100', textColor: 'text-gray-800', shadowColor: 'shadow-gray-100' },
  Confirmed: { color: 'sky', bgColor: 'bg-sky-100', textColor: 'text-sky-800', shadowColor: 'shadow-sky-100' },
  'In Progress': { color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-800', shadowColor: 'shadow-blue-100' },
  Billing: { color: 'amber', bgColor: 'bg-amber-100', textColor: 'text-amber-800', shadowColor: 'shadow-amber-100' },
  Completed: { color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-800', shadowColor: 'shadow-green-100' }
} as const;

export const DELIVERY_STATUS_CONFIG = {
  Draft: { color: 'gray', bgColor: 'bg-gray-100', textColor: 'text-gray-800' },
  Production: { color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-800' },
  Delivered: { color: 'indigo', bgColor: 'bg-indigo-100', textColor: 'text-indigo-800' },
  Approved: { color: 'teal', bgColor: 'bg-teal-100', textColor: 'text-teal-800' },
  Rejected: { color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-800' },
  Published: { color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-800' }
} as const;

export interface MohitInvoice {
  id: string;
  status: CompanyInvoiceStatus;
  invoice_number: string;
  purchase_order: string;
  issued_date: string;
  due_date: string;
  amount_received: number;
  payment_received_date: string;
  client_invoice_number: string;
  /** Avenças: the month this invoice bills, "YYYY-MM" */
  period?: string;
}

export type PayableInvoiceSource = 'influencer' | 'service';

export interface InfluencerInvoice {
  id: string;
  invoice_number: string;
  issue_date: string;
  due_date: string;
  amount: number;
  status: InfluencerInvoiceStatus;
  payment_received_date: string;
  source_type?: PayableInvoiceSource;
  source_id?: string;
  source_name?: string;
  /** Ids of the client (mohit) invoices that fund this payable — manual cross-check links. */
  funded_by_ids?: string[];
}

/**
 * Occasional line item on a campaign.
 * 'deduction'   — a cost subtracted from what we transfer to the creator.
 * 'passthrough' — charged to the client and paid straight to the creator,
 *                 so it earns no commission.
 */
export interface CampaignExpense {
  id: string;
  description: string;
  amount: number;
  kind?: 'deduction' | 'passthrough';
  /** Payable tranche it was settled with ('external': settled outside the app) */
  settled_in?: string | null;
  settled_date?: string | null;
}

export interface CampaignWithComputed extends Campaign {
  commission_amount: number;
  net_fee_agency: number;
  net_fee_influencer: number;
  days_remaining_to_due: number | null;
  client_payment_status: ClientPaymentStatus;
  company_invoice_overdue_days: number | null;
  row_color: 'green' | 'red' | 'orange' | 'yellow' | 'grey' | null;
  campaign_status: CampaignStatus;
  client_confirmed: boolean;
  show_billing_tag: boolean;
}

export interface CampaignFilters {
  search?: string;
  year?: number;
  brand?: string;
  influencer?: string;
  agent_name?: string;
  campaign_status?: CampaignStatus;
  company_invoice_status?: CompanyInvoiceStatus;
  influencer_invoice_status?: InfluencerInvoiceStatus;
  client_payment_status?: ClientPaymentStatus;
  delivery_status?: DeliveryStatus;
  invoice_number?: string;
}

export interface DashboardStats {
  ongoing_campaigns: number;
  active_clients: number;
  active_influencers: number;
  pending_invoices: number;
}

export interface KpiStats {
  campaigns_active: number;
  campaigns_consultation: number;
  campaigns_in_progress: number;
  invoices_not_issued: number;
  invoices_overdue: number;
  payments_ready: number;
  payments_ready_amount: number;
  total_commissions_expected?: number;
  /** In progress or billing: the campaigns being worked on. */
  campaigns_running?: number;
  /** Invoice requests waiting to be issued (any year). */
  invoice_requests_pending?: number;
  invoice_requests_amount?: number;
  invoice_request_campaign_ids?: string[];
}

// Re-export status helpers from constants
export { statusOrder as STATUS_ORDER, maxStatus } from '../constants/status';

// Helper to check if transition is forward
export function isForwardTransition(from: CampaignStatus, to: CampaignStatus): boolean {
  return statusOrder[to] >= statusOrder[from];
}
